/* eslint-disable max-lines, react/jsx-props-no-spreading */
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  discardAllocationOfLineItemsApi,
  inventoryLotsByLotIdApi,
  lineItemActiveAllocationsApi,
  listAvailableLotsForDispenseApi,
  listRecordableLotsForProductApi,
} from 'api/inventory';
import PatientBanner from 'common/modules/PatientBanner';
import { AddManualLotEnabled, BlockUnusableLotItems } from 'common/utils';
import { format } from 'date-fns';
// The SAME rule the receiving flow applies, imported rather than restated. Two copies of "is
// this lot number owned by another NDC" is how the four divergent NDC chains happened.
import {
  LOT_NUMBER_MAX_LENGTH,
  NO_PASTE_INPUT_PROPS,
  UNSAFE_LOT_CHARACTERS,
  getDispensableBudHelperText,
  getDispensableBudMinIso,
  getLotConfirmHelperText,
  getLotEntriesForNdc,
  getLotHelperText,
  getLotNdcMismatch,
  isLotBudExpired,
  isLotConfirmed,
  isValidLotNumber,
  lotConfirmFieldError,
  pickLotForBud,
} from 'modules/StockManagement/utils/stockLineValidation';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import ActiveAllocationsPanel from './ActiveAllocationsPanel';
import DiscardAllocationConfirmDialog from './DiscardAllocationConfirmDialog';
import {
  buildManualLot,
  computeAutoSelectedLots,
  getSelectedNdc,
  hasManualLot,
  isManualLot,
  lotKey,
} from './fillProductLotSelection';

const DISCARD_ALLOCATION_ERROR_MESSAGES = {
  LINE_ITEM_NOT_IN_CALLERS_FULFILLING_PHARMACY:
    "This item isn't in your fulfilling pharmacy — you can't act on it here.",
  FAILED_TO_DISCARD_ALLOCATION_OF_LINE_ITEMS: 'Failed to discard allocation. Please try again.',
};

const getDiscardAllocationErrorMessage = (err) => {
  const code = err?.response?.errors?.[0]?.extensions?.code;
  return (
    DISCARD_ALLOCATION_ERROR_MESSAGES[code] ?? 'Failed to discard allocation. Please try again.'
  );
};

const FulfillmentLockedBanner = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      px: 2,
      py: 1.75,
      bgcolor: '#FFF7ED',
      border: '1px solid #FED7AA',
      borderLeft: '3px solid #D97706',
      borderRadius: '6px',
    }}
  >
    <LockIcon sx={{ fontSize: '1.1rem', color: '#D97706', flexShrink: 0, mt: 0.1 }} />
    <Box>
      <Typography variant='body2' sx={{ fontWeight: 700, color: '#92400E' }}>
        Locked for Physical Count
      </Typography>
      <Typography variant='caption' sx={{ color: '#B45309', display: 'block' }}>
        This item is currently under a physical count workflow and fulfillment is blocked. You can
        fulfill once the count is complete. For further assistance, contact your administrator.
      </Typography>
    </Box>
  </Box>
);

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr), 'MM/dd/yyyy');
  } catch {
    return dateStr;
  }
};

const formatQty = (qty) => Math.round(parseFloat(qty) || 0);

const ExpiryChip = ({ daysUntilExpiry }) => {
  if (daysUntilExpiry <= 30) {
    return (
      <Chip
        size='small'
        label={`Expiring · ${daysUntilExpiry}d`}
        icon={<WarningAmberIcon />}
        sx={{
          height: 20,
          fontSize: '0.68rem',
          fontWeight: 600,
          bgcolor: '#FFF4D6',
          color: '#8A6100',
          '& .MuiChip-icon': { color: '#C77700', fontSize: '0.85rem', ml: '4px' },
        }}
      />
    );
  }
  return (
    <Typography variant='caption' color='text.secondary'>
      {daysUntilExpiry}d left
    </Typography>
  );
};

ExpiryChip.propTypes = {
  daysUntilExpiry: PropTypes.number.isRequired,
};

// Long enough that a whole lot number is typed as one query rather than one per keystroke. A lot
// number is read off a vial and typed in one go, so the operator is not waiting on this between
// characters - only after the last one.
const LOT_LOOKUP_DEBOUNCE_MS = 1500;

// Date-only, UTC, to match the BE and the create-time allocation check further down - the same
// reasoning that comment already records. A lot is dispensable through the end of its expiry date.
const todayIsoString = () => new Date().toISOString().split('T')[0];

const FillProductLotDialog = ({
  open,
  onClose,
  onConfirm,
  item,
  fulfillingPharmacyId,
  mpi,
  isConfirming,
}) => {
  const qtyToFill = parseFloat(item?.quantity) || 0;

  const [selectedLots, setSelectedLots] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [lotsData, setLotsData] = useState(null);
  // The last UNFILTERED lot list. lotsData follows the lot-number search box, so an operator who
  // searched, found nothing and opened manual entry would otherwise be offered no stocked lots in
  // the manual panel's list at all - exactly the case where they most need to see one.
  const [unfilteredLotItems, setUnfilteredLotItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // A fill must come from a single NDC. The user picks it via the NDC cards; the
  // active NDC's lots are the only ones selectable.
  const [activeNdc, setActiveNdc] = useState(null);
  const debounceRef = useRef(null);
  const hasAutoSelected = useRef(false);

  // POM-6083 — manual lot entry. The operator has the product in hand and the picker offered
  // nothing for it, so they type the lot instead. Note this is NOT limited to genuinely new lots:
  // a fully dispensed lot sits at remaining_qty 0 forever and the picker filters it out, so most
  // manual entries land on a lot that already exists. The back end decides which.
  const [manualOpen, setManualOpen] = useState(false);
  const [manualLotNumber, setManualLotNumber] = useState('');
  // Re-keyed by hand to confirm the first entry, exactly as receiving does. Only ever required
  // for a TYPED lot number: a lot picked from the list below carries no typo to catch, and a
  // re-key there is friction that teaches operators to work around the field.
  const [manualConfirmLotNumber, setManualConfirmLotNumber] = useState('');
  const [manualNdc, setManualNdc] = useState('');
  const [manualBud, setManualBud] = useState('');
  const [manualQty, setManualQty] = useState('');
  // POM-6083 - the option lists behind the panel: the product's inventory-master NDCs (a closed
  // list, because a claim against an NDC inventory never heard of cannot be reconciled) and its
  // zero-quantity lots (which the picker hides but which still exist as rows). Fetched lazily
  // when the panel opens, so an ordinary fill never pays for the query.
  const [recordable, setRecordable] = useState(null);
  const [isLoadingRecordable, setIsLoadingRecordable] = useState(false);
  // Lots carrying the typed number ACROSS EVERY NDC and facility.
  //
  // The dialog's own lot list is scoped to this product's NDCs, so it is structurally unable to
  // see a lot number that belongs to a different drug %s which is exactly the mistake being
  // caught: an operator reading a lot off the vial in their hand and typing it against the wrong
  // product. `lotNumber` records which value the entries answer for, so a reply that arrives
  // after the operator has typed on is not mistaken for an answer about the new value.
  const [foreignLookup, setForeignLookup] = useState({
    lotNumber: '',
    entries: [],
    loading: false,
    failed: false,
  });
  const foreignLookupSeq = useRef(0);
  // Why the closed lists are unavailable, when they are. Silently degrading to free text is
  // indistinguishable from the feature being broken, so the reason is shown in the panel.
  const [recordableNotice, setRecordableNotice] = useState(null);
  // Set when the operator PICKED one of those existing rows rather than typing a new number.
  // Claiming the real row is what stops one physical lot being split across two rows, with the
  // negative on one and the received history on the other.
  const [manualPickedLotId, setManualPickedLotId] = useState(null);

  // Pre-step: a line item may already carry active lot allocations from a prior
  // action. Check before letting the user pick lots again, so fulfillment can't
  // double-reserve inventory.
  const [allocationCheck, setAllocationCheck] = useState('idle'); // idle | loading | hasActive | none | error
  const [activeAllocations, setActiveAllocations] = useState([]);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  // A line item can already carry an active allocation while its product is still
  // locked for physical count (the lock may have been applied after the allocation
  // was made). Proceeding with that existing allocation must be blocked the same
  // way fresh lot selection is, so the lock is checked here too.
  const [isActiveAllocationLocked, setIsActiveAllocationLocked] = useState(false);
  // POM-5933: true when the create-time reserved lot has expired / is unavailable, so "Proceed
  // with Existing" is withheld and the user is dropped into the lot picker to choose a fresh lot.
  const [activeAllocationBlocked, setActiveAllocationBlocked] = useState(false);

  // POM-6083 — keyed via lotKey, not inventoryLotId directly: a manually entered lot has no id
  // yet and would otherwise collapse every manual row onto a single `null` key.
  const selectedById = new Map(selectedLots.map((l) => [lotKey(l), l]));

  // POM-6083 - a fill is EITHER allocated from available stock OR hand-recorded as unverified,
  // never both. Mixing them would put one line item's quantity across two buckets that are
  // deliberately never summed, so nothing downstream could say what was actually dispensed.
  // Exactly one of these is true at a time once anything is selected.
  const hasManualSelection = selectedLots.some((lot) => isManualLot(lot));
  const hasStockSelection = selectedLots.some((lot) => !isManualLot(lot));
  // Opening the panel IS the choice of mode, so the stock list is hidden for as long as the
  // panel is open or a hand-recorded lot is in the fill. Leaving it visible but disabled read
  // as "both are on offer", which is exactly what must not be possible.
  const manualMode = manualOpen || hasManualSelection;
  const totalAllocated = selectedLots.reduce(
    (sum, l) => sum + (parseFloat(l.qtyToAllocate) || 0),
    0
  );
  const remaining = qtyToFill - totalAllocated;

  // POM-6147 - the selected stock lot, and how much of the fill it cannot cover.
  //
  // Only ever a stock lot. A hand-recorded row is already a claim for its whole quantity, so it
  // has no shortfall to describe, and treating it as one would double-count the claim.
  const stockLot = selectedLots.find((lot) => !isManualLot(lot)) ?? null;
  const shortfallQty = stockLot
    ? Math.max(0, qtyToFill - (parseFloat(stockLot.qtyToAllocate) || 0))
    : 0;

  // POM-6147 - what goes on the wire for a short lot. The API is sent the FULL fill quantity
  // plus explicit permission to split it, rather than two entries for the same lot: it then
  // withdraws what the lot holds and claims the difference in one transaction, under one
  // availability guard. Sending the split ourselves would make the outcome depend on the order
  // of the array, and a concurrent fill draining the lot between the two entries would leave
  // the claim standing against stock somebody else took.
  //
  // qtyToAllocate is left as the stock figure - it is what the row displays.
  const lotsForConfirm =
    shortfallQty > 0 && stockLot
      ? selectedLots.map((lot) =>
          lotKey(lot) === lotKey(stockLot)
            ? { ...lot, fillQuantity: qtyToFill, allowShortfallAsUnverified: true }
            : lot
        )
      : selectedLots;

  // The NDC groups (one per NDC) drive the selector cards; resolve the active
  // group, falling back to the first group if the active NDC is no longer present
  // (e.g. after a lot-number search narrows the result).
  const groups = lotsData?.groups ?? [];
  const activeGroup = groups.find((g) => g.ndc === activeNdc) ?? groups[0] ?? null;
  const activeNdcResolved = activeGroup?.ndc ?? null;
  const groupQty = (group) =>
    (group?.lots ?? []).reduce((sum, l) => sum + (parseFloat(l.remainingQty) || 0), 0);

  const fetchLots = useCallback(() => {
    if (!open) return;
    // POM-5873: with nothing to query on (an unresolved productId, or no facility) we
    // cannot list lots — record it as an empty result rather than leaving lotsData null.
    // null would make hasNoLots false, which keeps Confirm Fill disabled forever; an
    // empty result routes the user to the unassigned fill instead.
    if (!item?.productId || !fulfillingPharmacyId) {
      setLotsData({ items: [], groups: [], totalCount: 0, isLocked: false });
      return;
    }
    setIsLoading(true);
    listAvailableLotsForDispenseApi({
      productId: item.productId,
      fulfillingPharmacyId,
      filter: debouncedSearch ? { lotNumber: debouncedSearch } : undefined,
    })
      .then((data) => {
        setLotsData(data);
        if (!debouncedSearch) setUnfilteredLotItems(data?.items ?? []);
        if (!hasAutoSelected.current && data?.items?.length > 0) {
          hasAutoSelected.current = true;
          // Auto-select a SINGLE NDC's lots: the default NDC when it has enough
          // quantity, otherwise the first NDC (FEFO) that does. Lots within the
          // chosen NDC are taken FEFO until qtyToFill is covered. The selector
          // opens on that chosen NDC (else the default/first group).
          const preSelected = computeAutoSelectedLots(data.items, qtyToFill);
          if (preSelected.length > 0) {
            setSelectedLots(preSelected);
          }
          const initialNdc =
            getSelectedNdc(preSelected) ??
            (data.groups ?? []).find((g) => g.isDefault)?.ndc ??
            data.groups?.[0]?.ndc ??
            null;
          setActiveNdc(initialNdc);
        }
      })
      .catch(() => setLotsData({ items: [], groups: [], totalCount: 0, isLocked: false }))
      .finally(() => setIsLoading(false));
  }, [open, item?.productId, fulfillingPharmacyId, debouncedSearch, qtyToFill]);

  const checkActiveAllocations = useCallback(() => {
    if (!open) return;
    setAllocationCheck('loading');
    setIsActiveAllocationLocked(false);
    setActiveAllocationBlocked(false);
    lineItemActiveAllocationsApi(item?.lineItemId)
      .then((allocations) => {
        if (!open) return;
        if (allocations.length > 0) {
          // POM-5933: if the create-time reserved lot has expired (BUD before today) or is no
          // longer available, don't offer "Proceed with Existing" — drop to the lot picker (which
          // excludes expired lots) so the user knowingly picks a fresh lot. Single-fill never
          // silently swaps the lot; the BE also blocks the fill as the authoritative guard. Date
          // compared date-only (yyyy-mm-dd) to match the BE and avoid timezone drift.
          const todayStr = todayIsoString();
          const hasBlockedLot = allocations.some((allocation) => {
            const expStr = allocation.bud
              ? new Date(allocation.bud).toISOString().split('T')[0]
              : null;
            return !expStr || expStr < todayStr;
          });
          if (hasBlockedLot) {
            setActiveAllocationBlocked(true);
            setAllocationCheck('none');
            fetchLots();
            return;
          }
          // The lock is a product/facility-level flag, independent of the
          // allocation itself, so it's checked separately here rather than
          // relying on the lot-selection fetch (which never runs in this branch).
          // The allocation isn't revealed as 'hasActive' until this resolves, so
          // Confirm Fill never has a moment where it's enabled before the lock
          // status is known.
          if (item?.productId && fulfillingPharmacyId) {
            listAvailableLotsForDispenseApi({
              productId: item.productId,
              fulfillingPharmacyId,
            })
              .then((data) => {
                if (!open) return;
                setIsActiveAllocationLocked(Boolean(data?.isLocked));
                setActiveAllocations(allocations);
                setAllocationCheck('hasActive');
              })
              .catch(() => {
                if (!open) return;
                setActiveAllocations(allocations);
                setAllocationCheck('hasActive');
              });
          } else {
            setActiveAllocations(allocations);
            setAllocationCheck('hasActive');
          }
        } else {
          setAllocationCheck('none');
          fetchLots();
        }
      })
      .catch(() => {
        if (open) setAllocationCheck('error');
      });
    // fetchLots is intentionally not a dependency — this only needs *a* current
    // fetchLots to call once the check resolves, not to re-run the allocation
    // check whenever the (not-yet-rendered) lot search term changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.lineItemId, item?.productId, fulfillingPharmacyId]);

  useEffect(() => {
    checkActiveAllocations();
  }, [checkActiveAllocations]);

  const handleProceedWithExisting = () => onConfirm({ selectedLots: [], unassigned: false });

  const handleDiscardConfirm = () => {
    setIsDiscarding(true);
    discardAllocationOfLineItemsApi(item?.lineItemId)
      .then(() => {
        enqueueSnackbar('Allocation discarded', { variant: 'success' });
        setShowDiscardConfirm(false);
        setActiveAllocations([]);
        setAllocationCheck('none');
        fetchLots();
      })
      .catch((err) => enqueueSnackbar(getDiscardAllocationErrorMessage(err), { variant: 'error' }))
      .finally(() => setIsDiscarding(false));
  };

  useEffect(() => {
    // Reacts only to changes in fetchLots' own inputs (search term, productId, etc.)
    // once lot selection is already the active step. The transition INTO that step —
    // the initial empty allocation check, or a successful discard — calls fetchLots
    // explicitly above instead, so it isn't fetched a second time here.
    if (allocationCheck === 'none') fetchLots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchLots]);

  // POM-6083 - the dialog stays mounted between opens, so every piece of manual-entry state has
  // to be cleared explicitly. Left behind, it reappears over the loading skeletons on the next
  // open: a lot number, BUD and quantity belonging to the PREVIOUS fill, one click away from
  // being recorded against this one.
  const resetManualEntry = useCallback(() => {
    setManualOpen(false);
    setManualLotNumber('');
    setManualConfirmLotNumber('');
    setManualNdc('');
    setManualBud('');
    setManualQty('');
    setManualPickedLotId(null);
    // Cleared too: the NDC list and depleted lots belong to one product, and this dialog can be
    // reopened for another. Refetching costs one lazy query when the panel is next opened.
    setRecordable(null);
    setIsLoadingRecordable(false);
    setRecordableNotice(null);
    setForeignLookup({ lotNumber: '', entries: [], loading: false, failed: false });
  }, []);

  useEffect(() => {
    if (!open) {
      resetManualEntry();
      setSelectedLots([]);
      setSearchValue('');
      setDebouncedSearch('');
      setLotsData(null);
      setUnfilteredLotItems([]);
      setActiveNdc(null);
      hasAutoSelected.current = false;
      setAllocationCheck('idle');
      setActiveAllocations([]);
      setIsActiveAllocationLocked(false);
      setShowDiscardConfirm(false);
      setIsDiscarding(false);
    }
  }, [open, resetManualEntry]);

  // Belt and braces: if this dialog instance is ever reused for a different line item without
  // unmounting, the stale panel would carry the previous product's lot into the new fill.
  useEffect(() => {
    resetManualEntry();
  }, [item?.productId, item?.lineItemId, resetManualEntry]);

  const handleSearch = (e) => {
    const { value } = e.target;
    setSearchValue(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, 350);
  };

  const handleSelectLot = (lot) => {
    if (selectedById.has(lotKey(lot))) return;
    // POM-6147 - a fill draws from ONE lot, so picking a lot REPLACES the selection rather than
    // adding to it. The quantity is not the operator's to choose either: it is everything this
    // lot can give, up to the fill. Where that is short of the prescription, the difference is
    // recorded as an unverified claim against this same lot - stated in the dialog, and asked
    // for explicitly on the wire.
    const qty = Math.min(parseFloat(lot.remainingQty) || 0, qtyToFill);
    if (qty <= 0) return;
    setSelectedLots([{ ...lot, qtyToAllocate: qty }]);
  };

  // Picking a different NDC card chooses a different NDC for the fill: discard the
  // current allocation and auto-fill the newly chosen NDC's lots FEFO to cover qty.
  const handleSelectNdc = (ndc) => {
    if (ndc === activeNdcResolved) return;
    setActiveNdc(ndc);
    const ndcItems = (lotsData?.items ?? []).filter((l) => l.ndc === ndc);
    setSelectedLots(computeAutoSelectedLots(ndcItems, qtyToFill));
  };

  const handleRemoveLot = (key) => {
    setSelectedLots((prev) => prev.filter((l) => lotKey(l) !== key));
  };

  // The NDC a manual entry is recorded against. Falls back to the line item's own NDC because
  // listAvailableLotsForDispense only returns NDCs that have stock — when the product has none,
  // there are no NDC cards to read one from, which is exactly when manual entry is needed most.
  const ndcOptions = recordable?.ndcOptions ?? [];
  // Used only once the query has settled. Falling back to the line item's NDC WHILE the request
  // is in flight shows a value that is about to be replaced by the product's default NDC - the
  // operator reads a field that then changes under them, and cannot tell which one was recorded.
  // Only ever an NDC the operator chose from the list. There is deliberately NO fallback to the
  // line item's own NDC: an NDC inventory has no master row for cannot be reconciled by anyone
  // downstream, and core hard-fails on it anyway (INVENTORY_ITEM_NOT_FOUND), so offering it
  // here would only let the operator fill the panel in and be rejected on submit.
  const manualNdcResolved = manualNdc;

  const todayIso = todayIsoString();

  const typedLot = manualLotNumber.trim();

  // Lots for the same NDC that still HAVE quantity. Offered in the panel's lot list with their
  // available quantity, because an operator who opened manual entry for a lot that is actually in
  // stock otherwise has to close the panel and go hunting for it. Withheld while the product is
  // count-locked: stock cannot be allocated then anyway.
  const stockedLotChoices = lotsData?.isLocked
    ? []
    : unfilteredLotItems.filter(
        (lot) =>
          lot.ndc === manualNdcResolved &&
          parseFloat(lot.remainingQty) > 0 &&
          lot.daysUntilExpiry >= 0
      );
  const isStockedChoice = (option) =>
    typeof option !== 'string' && !!option && parseFloat(option.remainingQty) > 0;
  // The stocked lot the lot number field names, whether picked from the list or typed in full.
  // Such a lot is not recorded by hand: Add lot allocates it from stock instead
  // (handleAddManualLot). Matched on the number alone, like the picker's own search, because a
  // number that resolves to a real row of this NDC carries no typo for a re-key to catch.
  const matchedStockLot = (() => {
    const typed = typedLot.toLowerCase();
    if (!typed || manualPickedLotId) return null;
    return stockedLotChoices.find((lot) => String(lot.lotNumber).toLowerCase() === typed) ?? null;
  })();

  /**
   * POM-6121 — is there a question to ask at all?
   *
   * Not for a lot taken from the list: that row is already this NDC's, so the only answer the
   * lookup could give is one refusing a lot inventory itself holds here. Not for a number the
   * format rule already refuses either — the panel reports the format first, so the answer would
   * be discarded whatever it said.
   *
   * Decided once and read by the effect and by the results below, so a value that is never looked
   * up cannot read as a lookup still running and hold Add lot for an answer never coming.
   */
  const shouldCheckForeignLot =
    manualOpen &&
    !!typedLot &&
    !manualPickedLotId &&
    !matchedStockLot &&
    isValidLotNumber(typedLot);

  // Debounced: this fires on the lot-number field, so without it every keystroke of a
  // 9-character lot number is its own round trip. The pause is measured against how a lot number
  // is entered — read off the vial and typed straight through — so it coalesces the whole entry
  // into one query and the operator only ever waits after the last character.
  useEffect(() => {
    if (!shouldCheckForeignLot) {
      setForeignLookup({ lotNumber: '', entries: [], loading: false, failed: false });
      return undefined;
    }

    setForeignLookup((prev) =>
      prev.lotNumber === typedLot
        ? prev
        : { lotNumber: typedLot, entries: [], loading: true, failed: false }
    );

    const seq = foreignLookupSeq.current + 1;
    foreignLookupSeq.current = seq;

    const timer = setTimeout(() => {
      inventoryLotsByLotIdApi({ lotId: typedLot })
        .then((entries) => {
          if (foreignLookupSeq.current !== seq) return;
          setForeignLookup({
            lotNumber: typedLot,
            entries: entries ?? [],
            loading: false,
            failed: false,
          });
        })
        .catch(() => {
          if (foreignLookupSeq.current !== seq) return;
          /* POM-6121 — blocking, not "nothing found". The comment here used to say core refuses
             the lot regardless; it does not. Core resolves a lot within the NDC's own inventory
             item, so a number belonging to another NDC never matches and a fresh lot is created
             under this one instead. This check is the only thing standing there, so a failed
             check refuses rather than passes. */
          setForeignLookup({ lotNumber: typedLot, entries: [], loading: false, failed: true });
        });
    }, LOT_LOOKUP_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [shouldCheckForeignLot, typedLot]);

  // Only trusted while it answers for the value currently typed.
  const foreignLookupIsCurrent = foreignLookup.lotNumber === typedLot;
  const foreignLookupAnswered = shouldCheckForeignLot && foreignLookupIsCurrent;
  const foreignNdcLots = foreignLookupAnswered
    ? getLotNdcMismatch({ entries: foreignLookup.entries, ndc: manualNdcResolved })
    : [];
  const foreignLookupFailed = foreignLookupAnswered && foreignLookup.failed;

  /**
   * POM-6121 — an existing lot for THIS NDC whose own BUD has passed.
   *
   * Read off the same lookup, so it covers a number typed as well as one taken from the list. A
   * claim records a dispense and an expired lot cannot be dispensed, so it is refused however the
   * number arrived — the fix is an inventory correction, not a fill.
   */
  const expiredSameNdcLot = (() => {
    if (!foreignLookupAnswered) return null;

    // The lookup is cross-FACILITY as well as cross-NDC, and one lot number legitimately sits at
    // two sites under different dates. Narrowed to this fill's own site first, the way
    // pickLotForBud already narrows when it adopts a BUD — otherwise another pharmacy's dead row
    // refuses a fill against stock that is in date here, and names a date nobody here can see.
    const sameNdc = getLotEntriesForNdc({ entries: foreignLookup.entries, ndc: manualNdcResolved });
    const lotOnFile = pickLotForBud({
      entries: sameNdc,
      receivedAtPharmacyId: fulfillingPharmacyId,
    });

    return isLotBudExpired({ expirationDate: lotOnFile?.expirationDate, todayStr: todayIso })
      ? lotOnFile
      : null;
  })();

  // Pending, not wrong. Kept OUT of manualError on purpose: that renders a warning Alert, and a
  // warning reads as something being amiss rather than merely unfinished - the same reasoning the
  // isLoadingRecordable suppression below already applies. This only disables the button and puts
  // it in a loading state, so nothing is ever added on an unverified lot number.
  const foreignCheckPending =
    shouldCheckForeignLot && (!foreignLookupIsCurrent || foreignLookup.loading);

  const describeForeignOwners = (lots) =>
    lots
      .map((entry) =>
        entry.drugName ? `${entry.drugName} (NDC ${entry.ndc})` : `NDC ${entry.ndc}`
      )
      .join(', ');

  const foreignLotMessage = (lots, lotNumber) =>
    `Lot ${lotNumber} already belongs to ${describeForeignOwners(lots)}. A lot number belongs to ` +
    `one product, so either the drug or the lot number is wrong — check the vial.`;
  // A closed NDC list is only offered when inventory actually has master rows for the product.
  // With none, the operator must still be able to record what they dispensed, so the field falls
  // back to free text seeded with the line item's own NDC.
  // Hand-recording needs an NDC inventory already knows. With none on offer the panel cannot be
  // used at all — the way forward is an unassigned fill, or an inventory user adding the product.
  const hasNdcChoices = ndcOptions.length > 0;

  // Zero-quantity lots for the NDC in the field. Scoped by NDC because a lot number is only
  // unique within one: offering another NDC's lots invites claiming stock of the wrong product.
  const depletedLotChoices = (recordable?.depletedLots ?? []).filter(
    (lot) => lot.ndc === manualNdcResolved
  );
  // Stocked first: groupBy needs the options already grouped, and a lot that can be allocated is
  // the better answer whenever it is the one in the operator's hand.
  const manualLotChoices = [
    ...stockedLotChoices,
    ...depletedLotChoices.filter(
      (lot) => !stockedLotChoices.some((s) => s.inventoryLotId === lot.inventoryLotId)
    ),
  ];
  const pickedLot = depletedLotChoices.find((lot) => lot.inventoryLotId === manualPickedLotId);
  // A lot with no BUD is NON-DISPENSABLE server-side (assessLotDispensability returns
  // BUD_MISSING), so it stays unusable until someone supplies the date — and the operator
  // holding the vial can read it off the label. So the field is editable for a picked lot that
  // has no BUD, and locked for one that does: changing an existing BUD is a correction to
  // inventory master data, which needs a reason and an approver and is refused by the API.
  const pickedLotNeedsBud = Boolean(manualPickedLotId) && !pickedLot?.expirationDate;

  /**
   * POM-6121 — a picked lot whose own BUD has passed.
   *
   * The option is disabled in the list, so this is the second line: it reads the DATE rather than
   * the `isExpired` flag the list renders, and the two can disagree — the flag was computed when
   * the query ran, the date is the lot's own. Its BUD is not the operator's to fix, so the field
   * is not flagged; the claim is refused all the same, because it records a dispense and expired
   * stock cannot go out. The fix is an inventory correction, not a fill.
   */
  const expiredPickedLot = isLotBudExpired({
    expirationDate: pickedLot?.expirationDate,
    todayStr: todayIso,
  })
    ? pickedLot
    : null;

  // The product's default NDC, else the first offered. One definition, because the panel needs
  // it in two places — after a fetch, and on every reopen once the options are cached.
  const preferredNdc = (options = []) =>
    (options.find((option) => option.isDefault) ?? options[0])?.ndc ?? '';

  const loadRecordableOptions = () => {
    // Nothing to query on. POM-5873: an OTC or unenriched line item can reach here with no
    // productId, and the same guard is why the lot picker below reports no lots. Stated rather
    // than returning quietly - a missing request looks exactly like a broken feature.
    if (!item?.productId || !fulfillingPharmacyId) {
      setRecordableNotice(
        // Precisely what is known: the line item carries no product id, so nothing was asked of
        // inventory. Saying "inventory has no record" would assert something never checked.
        AddManualLotEnabled
          ? 'This item has no product on the order line, so its NDCs cannot be looked up. Ask an inventory user to add the product to this order line.'
          : 'This item has no product on the order line, so its NDCs cannot be looked up. Record it as an unassigned fill instead.'
      );
      return;
    }
    setIsLoadingRecordable(true);
    setRecordableNotice(null);
    listRecordableLotsForProductApi({
      productId: item.productId,
      fulfillingPharmacyId,
    })
      .then((data) => {
        setRecordable(data);
        if (!data.hasInventoryMaster) {
          setRecordableNotice(
            AddManualLotEnabled
              ? 'Inventory has no NDC on record for this product, so a lot cannot be recorded against it. Ask an inventory user to add the product.'
              : 'Inventory has no NDC on record for this product, so a lot cannot be recorded against it. Ask an inventory user to add the product, or record this as an unassigned fill.'
          );
        }
        // Default to the product's default NDC once the list is known, but never overwrite a
        // choice the operator has already made while the request was in flight.
        setManualNdc((current) => {
          if (current && data.ndcOptions.some((option) => option.ndc === current)) return current;
          return preferredNdc(data.ndcOptions) || current;
        });
      })
      // A failed lookup must not block recording - the operator has the product in hand and the
      // claim is validated server-side either way - but it must not pass unremarked either.
      // `recordable` is deliberately left null so the next open retries instead of caching the
      // failure for the life of the dialog.
      .catch(() =>
        setRecordableNotice(
          'Could not load the NDCs for this product, so a lot cannot be recorded by hand. Close and reopen the panel to retry.'
        )
      )
      .finally(() => setIsLoadingRecordable(false));
  };

  const openManualEntry = () => {
    // POM-6083 gate. The trigger button is not rendered when the switch is off, so this
    // is unreachable today - it is here so that a future caller (an empty-state CTA, a
    // keyboard shortcut) cannot reopen the feature without also reopening the gate. It
    // also keeps the listRecordableLotsForProduct request from being made at all, since
    // this function is its only caller.
    if (!AddManualLotEnabled) return;
    // Prefill from the search box: the operator has almost always just typed the lot number
    // looking for it, and retyping it is the kind of friction that produces typos in a lot
    // number nobody can correct later.
    setManualLotNumber(searchValue.trim());
    // Never prefilled from the search box, even though the lot number is. The whole value of the
    // second field is that it is typed independently; seeding it would confirm the search term
    // against itself.
    setManualConfirmLotNumber('');
    // Seeded from the cached options when they are already loaded. Clearing it and relying on
    // loadRecordableOptions to re-derive the default only works on the FIRST open: `recordable`
    // is cached for the life of the dialog, so every later open fell through to the line item's
    // raw NDC instead. That NDC need not be one inventory knows, which silently emptied the
    // depleted-lot list (filtered by NDC) and risked submitting a mismatched NDC.
    setManualNdc(recordable ? preferredNdc(recordable.ndcOptions) : '');
    setManualBud('');
    // Counted against the HAND-RECORDED lots only, because the stock allocation is cleared
    // just below. Using totalAllocated here read the quantity that is about to be discarded,
    // seeding 0 from a fully allocated fill and leaving Add lot permanently disabled.
    const manualAllocated = selectedLots
      .filter((lot) => isManualLot(lot))
      .reduce((sum, lot) => sum + (parseFloat(lot.qtyToAllocate) || 0), 0);
    setManualQty(String(Math.max(0, qtyToFill - manualAllocated) || ''));
    setManualPickedLotId(null);
    // Switching mode: drop anything allocated from stock. The operator asked to record by
    // hand, and a fill is one or the other - so this is the answer to the request rather than
    // an error telling them to go and undo it themselves, row by row.
    setSelectedLots((prev) => prev.filter((lot) => isManualLot(lot)));
    setManualOpen(true);
    if (!recordable) loadRecordableOptions();
  };

  // A stray click on the backdrop must not discard the dialog: by this point the operator may
  // have picked lots across several NDCs or typed a lot number and BUD that exist nowhere else
  // yet, and none of it is recoverable. Closing is deliberate only - the Cancel button, or Esc.
  const handleDialogClose = (_event, reason) => {
    if (reason === 'backdropClick') return;
    onClose();
  };

  const closeManualEntry = () => {
    setManualOpen(false);
    setRecordableNotice(null);
    setManualLotNumber('');
    setManualConfirmLotNumber('');
    setManualBud('');
    setManualQty('');
    setManualPickedLotId(null);
    setForeignLookup({ lotNumber: '', entries: [], loading: false, failed: false });
  };

  // Picking an existing row adopts its identity wholesale: its lot number and its BUD. Letting
  // the operator then edit the BUD would describe a different lot than the one being claimed.
  const handlePickDepletedLot = (value) => {
    if (typeof value === 'string' || value === null) {
      setManualLotNumber(typeof value === 'string' ? value : '');
      // The confirmation is deliberately NOT cleared here. This branch fires on every blur of the
      // free-text field, including one that changed nothing, so clearing would wipe a correct
      // re-key just for tabbing back through. A confirmation answers for one exact value, and a
      // value that has since been edited simply fails isLotConfirmed — which is the honest
      // outcome, rather than an empty field that looks untouched.
      // Only drop the BUD if it was ADOPTED from a previously picked lot — that value described
      // the picked row and is meaningless once the row is abandoned. A BUD the operator typed
      // themselves is theirs to keep: this branch fires on every committed edit or blur of the
      // free-text lot number, not only on an actual pick, so clearing unconditionally wiped a
      // typed expiration date the moment the lot number was touched again.
      if (manualPickedLotId) setManualBud('');
      setManualPickedLotId(null);
      return;
    }
    setManualLotNumber(value.lotNumber);
    setManualConfirmLotNumber('');
    setManualPickedLotId(value.inventoryLotId);
    // A dated lot dictates the field. An undated one leaves whatever the operator has typed, so
    // reading the BUD off the vial before picking the lot from the list is not punished.
    if (value.expirationDate) {
      setManualBud(String(value.expirationDate).substring(0, 10));
    }
  };

  // A stocked lot picked from the list only fills in its number; matchedStockLot then carries its
  // identity, and nothing changes mode until Add lot is clicked.
  const handleManualLotChange = (_event, value) =>
    handlePickDepletedLot(isStockedChoice(value) ? value.lotNumber : value);

  // Switching NDC invalidates a picked lot: it belonged to the previous NDC's list.
  // Switching NDC invalidates everything already entered about the lot. A lot number is only
  // unique WITHIN an NDC, so carrying it across would record that number against a different
  // product NDC - claiming a lot that does not exist there, or creating a bogus row for it. The
  // BUD goes with it (it described the old lot) and so does any picked row, which belonged to
  // the previous NDC's list.
  const handleManualNdcChange = (ndc) => {
    // Re-selecting the same NDC is not a change and must not wipe work in progress.
    if (ndc === manualNdcResolved) return;

    setManualNdc(ndc);
    setManualLotNumber('');
    setManualConfirmLotNumber('');
    setManualBud('');
    setManualPickedLotId(null);
  };

  // Nothing a second hand-recorded lot could do. The panel's quantity is read-only and always the
  // whole remainder, so one manual lot covers the entire fill — reopening it can only ever reach
  // "The prescription is already fully allocated", after the operator has been shown a full set of
  // empty fields. Starting over means removing the recorded lot below, which the section's own ×
  // already does.
  //
  // Narrowed to a fill covered BY HAND on purpose: when the allocation came from stock, a fully
  // allocated fill is exactly when an operator discovers the picked lot is not what is in their
  // hand, and openManualEntry clears that allocation for them. That path has to stay open.
  const manualEntryExhausted = hasManualSelection && remaining <= 0;

  // A picked row's identity is already recorded, so there is nothing to re-key against it. Only a
  // number the operator typed themselves needs confirming.
  const needsLotConfirmation = !manualPickedLotId && !matchedStockLot;
  // True whenever the BUD field is something the operator can actually type into: a new lot, or a
  // picked lot that has no BUD of its own. An adopted date is not theirs to fix — the field is
  // disabled — so the dispensable-date rule cannot be applied to it.
  const budIsTyped = needsLotConfirmation || pickedLotNeedsBud;
  const budTooEarly = budIsTyped ? getDispensableBudHelperText(manualBud) : '';

  // Flattened out of the JSX: the BUD field says three different things depending on whether a
  // lot was picked and whether that lot has a BUD at all.
  const manualBudHelperText = (() => {
    // A date that cannot be dispensed outranks the rest: it is the one thing here that blocks the
    // Add, so it has to be what the field says.
    if (budTooEarly) return budTooEarly;
    if (matchedStockLot) return 'From the selected lot';
    if (!manualPickedLotId) return 'Optional — tomorrow or later';
    if (pickedLot?.expirationDate) return 'From the selected lot';
    // Actionable rather than merely descriptive: entering it here is what makes the lot
    // dispensable again.
    return 'This lot has no BUD — enter it from the label to record one';
  })();

  // The lot number field's ordinary guidance, for when the value breaks no format rule.
  const manualLotGuidance = (() => {
    if (matchedStockLot) {
      return `In stock — ${formatQty(
        matchedStockLot.remainingQty
      )} available. Add lot allocates it from stock.`;
    }
    if (manualPickedLotId) return 'Existing lot — the quantity is added to this lot';
    return 'Pick an existing lot or type a new one';
  })();

  // Every input in the panel is inert while it cannot be used. Disabling only the Add button
  // let the operator fill the whole form in and discover the refusal at the end.
  const manualFieldsDisabled = isLoadingRecordable || hasStockSelection || !hasNdcChoices;

  const manualQtyNum = parseFloat(manualQty) || 0;
  const manualDuplicate = hasManualLot(selectedLots, {
    lotNumber: manualLotNumber.trim(),
    ndc: manualNdcResolved,
    expirationDate: manualBud || null,
    inventoryLotId: manualPickedLotId,
  });
  // NDC is required and hard-fails server-side, so block here rather than letting the operator
  // submit into a rejection. Quantity must fit what is still needed: a manual lot has no known
  // stock to clamp against, so this input is the only guard against over-allocating.
  const manualError = (() => {
    // Nothing can be validated against a list that has not arrived: the NDC is unknown, so the
    // lot list is unknown, so whether a typed lot has stock is unknown.
    if (isLoadingRecordable) return 'Loading the NDCs for this product…';
    // A hard stop, not a field problem. The NDC has to be one inventory already knows, so with
    // no options there is nothing valid the operator could enter — and letting them type one
    // would just move the rejection to submit, after the whole panel had been filled in.
    if (!hasNdcChoices) {
      return AddManualLotEnabled
        ? 'No NDC is on record for this product, so a lot cannot be recorded by hand. Ask an inventory user to add the product before filling it.'
        : 'No NDC is on record for this product, so a lot cannot be recorded by hand. Record this as an unassigned fill instead.';
    }
    // Unreachable through the UI: openManualEntry clears the stock allocation and the stock
    // list is hidden while the panel is open. Kept as an invariant guard - if a future change
    // ever lets both coexist, this refuses the fill rather than splitting one line item's
    // quantity across two buckets that are deliberately never summed.
    if (hasStockSelection) {
      return 'Remove the lots allocated above first. A fill is either allocated from available stock or recorded by hand, not both.';
    }
    if (!manualLotNumber.trim()) return 'Enter the lot number.';
    // The same rule receiving applies, imported rather than restated. A lot number that reaches
    // the ledger with a space or a quote in it is one that will not match on the next scan.
    if (!isValidLotNumber(manualLotNumber)) {
      return (
        `Check the lot number — no spaces or ${UNSAFE_LOT_CHARACTERS}, ` +
        `up to ${LOT_NUMBER_MAX_LENGTH} characters.`
      );
    }
    if (!manualNdcResolved)
      return 'No NDC could be resolved for this product. Select an NDC first.';
    // A lot inventory holds stock for is allocated, not recorded by hand, so none of the checks
    // below (the cross-NDC lookup, the re-key, the typed BUD, the hand-recorded quantity) apply.
    // Add lot hands it to the stock allocation, which sizes and splits it itself.
    if (matchedStockLot) return null;
    // Shown once the debounced lookup has landed. Until then foreignCheckPending holds the
    // button disabled rather than surfacing anything here: an outstanding check is unfinished,
    // not a problem, and a warning Alert for it would read as something being wrong.
    // POM-6121 — a check that could not run is not a check that passed. Nothing on the server
    // refuses a lot number belonging to another NDC, so this is the only thing standing there.
    if (foreignLookupFailed) {
      return 'Could not check this lot number against inventory. Try again in a moment.';
    }
    if (foreignNdcLots.length > 0) {
      return foreignLotMessage(foreignNdcLots, manualLotNumber.trim());
    }
    // Picked or typed, the same lot and the same refusal: the number is only offered as a choice,
    // and typing past a disabled option must not be a way around it.
    const expiredLot = expiredPickedLot ?? expiredSameNdcLot;
    if (expiredLot) {
      return `Lot ${expiredLot.lotNumber} expired on ${String(expiredLot.expirationDate).slice(
        0,
        10
      )}. An expired lot cannot be dispensed, so it cannot be recorded by hand either.`;
    }
    // Ordered AFTER the foreign-lot and in-stock checks on purpose: both of those mean the number
    // itself is wrong, and asking for it to be typed a second time before saying so would have the
    // operator re-key a value that was never going to be accepted.
    if (needsLotConfirmation && !isLotConfirmed(manualLotNumber, manualConfirmLotNumber)) {
      return String(manualConfirmLotNumber ?? '').trim()
        ? 'The two lot numbers do not match. Re-enter it exactly as typed above.'
        : 'Re-enter the lot number to confirm it.';
    }
    // The input carries a min, so this is only reachable by typing a date directly into it.
    if (budTooEarly) {
      return 'The expiration date must be tomorrow or later — an expired lot cannot be dispensed.';
    }
    // Ordered BEFORE the quantity check on purpose. The quantity field is read-only and seeds to
    // whatever is still to allocate, so on a fully allocated fill it seeds empty and the generic
    // "enter a quantity greater than zero" would be a dead end — there is no field to correct.
    // This message names the actual fix.
    if (remaining <= 0) {
      // Distinguished from the over-allocation message below: nothing is left to record, and the
      // fix is to reduce an allocation above rather than to type a smaller number here.
      return 'The prescription is already fully allocated. Reduce or remove an allocation above to record a lot by hand.';
    }
    if (manualQtyNum <= 0) return 'Enter a quantity greater than zero.';
    if (manualQtyNum > Math.max(0, qtyToFill - totalAllocated)) {
      return `Only ${formatQty(Math.max(0, qtyToFill - totalAllocated))} units are still needed.`;
    }
    if (manualDuplicate) return 'This lot has already been added below.';
    return null;
  })();

  // No cross-NDC check of its own: foreignCheckPending keeps the button disabled until the
  // debounced lookup has answered for the value currently typed, so this can only ever run on a
  // verified lot number. Re-checking here fired a SECOND request on every click for an answer
  // already in hand.
  //
  // Why the check has to exist at all: core throws the same rejection, but
  // manageProductFulfillment CATCHES a failed allocation and lets the fulfilment proceed
  // unallocated - deliberately, so inventory can never block a fill. The operator therefore saw
  // no error whatsoever and the fill went out with nothing allocated. Only a check before submit
  // is visible to them.
  const handleAddManualLot = () => {
    if (manualError || foreignCheckPending) return;

    // A lot with stock goes back to the ordinary stock allocation, exactly as if it had been
    // picked in the lot list. The search is cleared so the lot is visible there: that list
    // follows the search box, and a search typed before opening the panel may well hide it.
    if (matchedStockLot) {
      const lot = matchedStockLot;
      closeManualEntry();
      clearTimeout(debounceRef.current);
      setSearchValue('');
      setDebouncedSearch('');
      setActiveNdc(lot.ndc);
      handleSelectLot(lot);
      return;
    }

    setSelectedLots((prev) => [
      ...prev,
      buildManualLot({
        lotNumber: manualLotNumber.trim(),
        ndc: manualNdcResolved,
        expirationDate: manualBud || null,
        quantity: manualQtyNum,
        inventoryLotId: manualPickedLotId,
        // Sent alongside a real lot id ONLY when that lot has no BUD — the API applies it to
        // the lot, and can find it names a lot that already exists, in which case it claims
        // that one instead. Not sent for an already-dated lot: the API refuses the change.
        suppliesBud: pickedLotNeedsBud && Boolean(manualBud),
      }),
    ]);
    closeManualEntry();
  };

  const isItemLocked = lotsData?.isLocked ?? false;
  const isExactAllocation = totalAllocated === qtyToFill;
  const isOverAllocated = totalAllocated > qtyToFill;

  const getAllocationColor = () => {
    if (isExactAllocation) return 'success.main';
    if (isOverAllocated) return 'error.main';
    return 'warning.main';
  };

  const getAllocationMessage = () => {
    if (isExactAllocation) return 'Allocation matches the prescription quantity.';
    if (isOverAllocated) return `Over-allocated by ${totalAllocated - qtyToFill} units.`;
    if (totalAllocated === 0) return `${qtyToFill} units to allocate.`;
    // POM-6147 - never "remaining to allocate": there is no second lot to allocate it from, and
    // nothing for the operator to go and fix. Say what will happen instead.
    if (shortfallQty > 0)
      return `${shortfallQty} units will be recorded as ${SYSTEM_DEFICIT.toLowerCase()}.`;
    return `${qtyToFill - totalAllocated} units remaining to allocate.`;
  };

  const progressValue = qtyToFill > 0 ? Math.min(100, (totalAllocated / qtyToFill) * 100) : 0;

  const hasNoLots =
    !isLoading && lotsData?.totalCount === 0 && selectedLots.length === 0 && !debouncedSearch;
  // Where manual lot entry is switched on, an unassigned fill is no longer an acceptable
  // outcome: the operator has the product in hand, so the lot can be recorded rather than the
  // fill going out non-lotted and waiting on an inventory audit to reconcile. The unassigned
  // route is the whole reason this feature exists, so the two are gated by the same switch.
  //
  // hasNoLots is the ONLY unassigned route through this button (an empty selection is already
  // refused when stock exists, and "Proceed with Existing" sends unassigned: false), so gating it
  // here closes the path rather than merely hiding it.
  // POM-6121 — gated on the lot-required switch, not on AddManualLotEnabled: recording a lot by
  // hand and REFUSING a fill without one are two capabilities that roll out at different times,
  // and this is now the one switch that governs the refusal on both screens.
  // Both switches, not either: BlockUnusableLotItems says a fill needs a lot, AddManualLotEnabled
  // is what gives the operator a way to supply one. Blocking without the remedy available would
  // leave them with no way forward at all, which is the POM-6083 OFF path and must stay intact.
  const unassignedFillBlocked = AddManualLotEnabled && BlockUnusableLotItems && hasNoLots;
  // POM-6147 - a shortfall is a stated outcome, so under-allocation no longer blocks the fill.
  // What blocks it is having selected nothing at all. Over-allocation is unreachable from the
  // stock list now that the quantity is derived rather than typed, but the guard stays for the
  // hand-recorded path, which still sums operator-entered quantities.
  const confirmDisabled =
    isItemLocked ||
    unassignedFillBlocked ||
    (!hasNoLots && (isOverAllocated || selectedLots.length === 0));

  // The active NDC's lots (FEFO ordered), including any selected ones so a lot kept
  // out by a lot-number search still shows while it holds an allocation.
  //
  // daysUntilExpiry === 0 means "expires today", which is still dispensable — the
  // whole point of FEFO is to use it before it goes. The backend is the real gate
  // and it already filters on `expirationDate >= today`, so nothing negative
  // reaches us; the check here only guards against that changing. It used to read
  // `> 0`, which hid today's lots from the list. Auto-selection has no expiry
  // filter, so such a lot arrived pre-selected and visible, then vanished the
  // moment it was unchecked — taking the "no available lots" message with it when
  // the NDC had nothing else.
  // Manually recorded rows are held separately from the FEFO list: they are not in lotsData
  // (they do not exist in inventory as available stock) and must not be presented as if they were.
  const manualLots = selectedLots.filter(isManualLot);

  const activeLots = (lotsData?.items || []).filter(
    (l) => l.ndc === activeNdcResolved && (l.daysUntilExpiry >= 0 || selectedById.has(lotKey(l)))
  );

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth='md'
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pt: 2.5, pb: 1.5, px: 3 }}>
        <Typography variant='h6' fontWeight={800} letterSpacing={-0.2}>
          Fill Product
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
        {/* Patient card */}
        <Box sx={{ mb: 2 }}>
          <PatientBanner mpi={mpi} />
        </Box>

        {/* Item metadata */}
        <Box
          sx={{
            p: 2,
            mb: 2.5,
            borderRadius: 2,
            bgcolor: (t) => alpha(t.palette.text.primary, 0.02),
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {[
            { label: 'Item', value: item?.itemName || '—', bold: true },
            { label: 'NDC', value: item?.ndc || '—' },
            { label: 'RX Number', value: item?.rxNumber || '—' },
            {
              label: 'Strength · Form',
              value: [item?.strength, item?.form].filter(Boolean).join('  ·  ') || '—',
            },
          ].map(({ label, value, bold }) => (
            <Box
              key={label}
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 1,
                mb: 0.75,
                '&:last-child': { mb: 0 },
              }}
            >
              <Typography variant='body2' color='text.secondary' sx={{ minWidth: 120 }}>
                {label} :
              </Typography>
              <Typography variant='body2' fontWeight={bold ? 600 : 400}>
                {value}
              </Typography>
            </Box>
          ))}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
            <Typography variant='body2' color='text.secondary' sx={{ minWidth: 120 }}>
              Qty to Fill :
            </Typography>
            <Chip
              label={item?.quantity || '—'}
              size='small'
              color='primary'
              sx={{ fontWeight: 700, fontSize: '0.8rem', height: 24 }}
            />
          </Box>
        </Box>

        {/* Active allocation pre-step */}
        {allocationCheck === 'loading' && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Skeleton variant='text' width='60%' sx={{ mx: 'auto', mb: 1 }} />
            <Skeleton variant='text' width='40%' sx={{ mx: 'auto' }} />
          </Box>
        )}

        {allocationCheck === 'error' && (
          <Alert
            severity='error'
            action={
              <Button color='inherit' size='small' onClick={checkActiveAllocations}>
                Retry
              </Button>
            }
          >
            Failed to check for existing allocations. Please try again.
          </Alert>
        )}

        {allocationCheck === 'hasActive' && (
          <>
            {isActiveAllocationLocked && (
              <Box sx={{ mb: 2 }}>
                <FulfillmentLockedBanner />
              </Box>
            )}
            <ActiveAllocationsPanel allocations={activeAllocations} />
          </>
        )}

        {allocationCheck === 'none' && (
          <>
            {/* POM-5933: the previously reserved lot expired / is unavailable — prompt a fresh pick. */}
            {activeAllocationBlocked && (
              <Alert severity='warning' sx={{ mb: 2 }}>
                The previously allocated lot has expired or is no longer available. Please select a
                new lot below.
              </Alert>
            )}

            {/* Locked banner */}
            {isItemLocked && (
              <Box sx={{ mb: 2 }}>
                <FulfillmentLockedBanner />
              </Box>
            )}

            {/* NDC selector + lot selection (hidden when locked) */}
            {!isItemLocked && (
              <>
                {/* NDC selector cards — one per NDC; the active card is the single NDC this fill draws from.
                    Hidden in manual mode: the panel carries its OWN required NDC field, and two NDC
                    selectors on screen at once invite the operator to set them to different values —
                    only the panel's would be sent. Matches the lot list and lot search, which already
                    hide on !manualMode for the same reason. */}
                {groups.length > 0 && !manualMode && (
                  <Box sx={{ mb: 2.5 }}>
                    <Typography
                      variant='overline'
                      sx={{ color: 'text.secondary', letterSpacing: 0.6, fontWeight: 700 }}
                    >
                      Select NDC
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, mt: 0.75, overflowX: 'auto', pb: 0.5 }}>
                      {groups.map((group) => {
                        const selected = group.ndc === activeNdcResolved;
                        return (
                          <Box
                            key={group.ndc}
                            role='button'
                            tabIndex={0}
                            aria-pressed={selected}
                            onClick={() => handleSelectNdc(group.ndc)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleSelectNdc(group.ndc);
                              }
                            }}
                            sx={{
                              flex: '0 0 auto',
                              minWidth: 200,
                              maxWidth: 240,
                              p: 1.5,
                              borderRadius: 2,
                              cursor: 'pointer',
                              border: '1.5px solid',
                              borderColor: selected ? 'primary.main' : 'divider',
                              bgcolor: (t) =>
                                selected ? alpha(t.palette.primary.main, 0.06) : 'background.paper',
                              transition: 'border-color 150ms ease, box-shadow 150ms ease',
                              '&:hover': {
                                borderColor: selected ? 'primary.main' : 'primary.light',
                                boxShadow: 1,
                              },
                              '&:focus-visible': {
                                outline: (t) => `2px solid ${t.palette.primary.main}`,
                                outlineOffset: 2,
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                  minWidth: 0,
                                }}
                              >
                                <Typography
                                  variant='body2'
                                  fontWeight={700}
                                  sx={{ fontFamily: 'monospace' }}
                                  noWrap
                                >
                                  {group.ndc}
                                </Typography>
                                {/* A star, matching the NDC dropdown in the manual-entry
                                    panel below. aria-label carries what the glyph means, which a
                                    bare ★ does not. */}
                                {group.isDefault && (
                                  <Box
                                    component='span'
                                    aria-label='Manufacturer default NDC'
                                    title='Manufacturer default'
                                    sx={{ color: 'primary.main', fontSize: 13, lineHeight: 1 }}
                                  >
                                    ★
                                  </Box>
                                )}
                              </Box>
                              {selected && (
                                <CheckCircleIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                              )}
                            </Box>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                              noWrap
                              sx={{ display: 'block', mt: 0.5 }}
                            >
                              {group.manufacturer || '—'}
                            </Typography>
                            <Typography
                              variant='caption'
                              sx={{
                                display: 'block',
                                mt: 0.5,
                                fontWeight: 600,
                                color: selected ? 'primary.main' : 'text.secondary',
                              }}
                            >
                              {formatQty(groupQty(group))} available
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {/* Lots header + search */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    mb: 1.25,
                  }}
                >
                  <Box>
                    <Typography variant='subtitle2' fontWeight={700}>
                      Lots to Fill
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {hasManualSelection
                        ? 'Unavailable while a lot is recorded by hand — remove it below to allocate from stock instead'
                        : `${
                            activeNdcResolved ? `NDC ${activeNdcResolved} · ` : ''
                          }FEFO ordered · earliest expiry first`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {!hasNoLots && !manualMode && (
                      <TextField
                        size='small'
                        placeholder='Search lot number...'
                        value={searchValue}
                        onChange={handleSearch}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              <SearchIcon fontSize='small' />
                            </InputAdornment>
                          ),
                          sx: { borderRadius: 2 },
                        }}
                        sx={{ width: 230 }}
                      />
                    )}
                    {AddManualLotEnabled && !manualOpen && !manualEntryExhausted && (
                      <Button
                        size='small'
                        startIcon={<AddIcon />}
                        onClick={openManualEntry}
                        // Available whenever the product is not locked for count. It used to be
                        // disabled once the prescription was fully allocated, but that is
                        // precisely when an operator discovers a picked lot is not what is in
                        // their hand - and a disabled button gives them nowhere to go. That
                        // reasoning does NOT cover a fill already covered by a hand-recorded lot:
                        // see manualEntryExhausted.
                        disabled={isItemLocked}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        Enter lot manually
                      </Button>
                    )}
                  </Box>
                </Box>

                {/* POM-6083 — manual lot entry. The operator has the product in hand but the
                    picker offered nothing for it. The quantity is recorded as a{' '}
                    {SYSTEM_DEFICIT.toUpperCase()}
                    claim: stock was dispensed that inventory never received. It does not add
                    to available stock, and a person has to confirm any return against it. */}
                {AddManualLotEnabled && manualOpen && (
                  <Box
                    sx={{
                      mb: 1.5,
                      p: 2,
                      border: '1px solid',
                      borderColor: 'warning.light',
                      borderRadius: 2,
                      bgcolor: (t) => alpha(t.palette.warning.main, 0.04),
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: 1.5,
                      }}
                    >
                      <Box>
                        <Typography variant='subtitle2' fontWeight={700}>
                          Record a lot manually
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                          For stock inventory has no quantity for. Pick one of this product&apos;s
                          zero-quantity lots or type a new lot number. Picking a lot that still has
                          quantity allocates it from stock instead. A hand-recorded quantity is
                          recorded as {SYSTEM_DEFICIT.toLowerCase()} until an inventory user enters
                          the receiving record.
                        </Typography>
                      </Box>
                      <IconButton
                        size='small'
                        onClick={closeManualEntry}
                        aria-label='Close manual entry'
                      >
                        <CloseIcon fontSize='small' />
                      </IconButton>
                    </Box>

                    {recordableNotice && (
                      <Alert severity='info' sx={{ mb: 1.5, py: 0.25 }}>
                        {recordableNotice}
                      </Alert>
                    )}

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
                      {/* NDC first: it scopes the lot list below, and a lot number is only
                          unique within one NDC. ALWAYS a closed list, never typeable — a claim
                          against an NDC inventory has no master row for cannot be reconciled by
                          anyone downstream, and core rejects it outright. Only the lot number is
                          free text here. Empty and disabled when there is nothing to offer, with
                          the reason stated in the alert below. */}
                      <TextField
                        select
                        size='small'
                        label='NDC'
                        required
                        value={manualNdcResolved}
                        onChange={(e) => handleManualNdcChange(e.target.value)}
                        disabled={manualFieldsDisabled || !hasNdcChoices}
                        helperText={
                          isLoadingRecordable
                            ? 'Loading the NDCs for this product…'
                            : 'Required — pick one inventory holds. ★ is the manufacturer default.'
                        }
                        // The closed field shows the NDC alone (★ for the default); the
                        // manufacturer is only in the open list, on its own row.
                        SelectProps={{
                          renderValue: (ndc) => {
                            const option = ndcOptions.find((o) => o.ndc === ndc);
                            return option?.isDefault ? `${ndc} ★` : ndc;
                          },
                        }}
                        sx={{ flex: '1 1 220px' }}
                      >
                        {ndcOptions.map((option) => (
                          <MenuItem key={option.ndc} value={option.ndc}>
                            <Box>
                              <Typography variant='body2' fontWeight={700}>
                                {option.ndc}
                                {option.isDefault ? ' ★' : ''}
                              </Typography>
                              {option.manufacturer && (
                                <Typography variant='caption' color='text.secondary'>
                                  {option.manufacturer}
                                </Typography>
                              )}
                            </Box>
                          </MenuItem>
                        ))}
                      </TextField>

                      {/* freeSolo so a genuinely new lot can still be typed, with autoSelect so
                          typed text is committed on blur — without it the value is discarded
                          unless the operator presses Enter, which silently loses the entry. */}
                      <Autocomplete
                        freeSolo
                        autoSelect
                        size='small'
                        options={manualLotChoices}
                        groupBy={(option) =>
                          isStockedChoice(option) ? 'Available stock' : 'Zero-quantity lots'
                        }
                        loading={isLoadingRecordable}
                        // POM-6121 — an expired lot cannot be dispensed, so it cannot be claimed.
                        // Offered but unselectable, so the operator can see it is on file and why
                        // it is not on offer.
                        getOptionDisabled={(option) =>
                          typeof option !== 'string' && !!option.isExpired
                        }
                        // The options are scoped by NDC, so until the NDC is known there is
                        // nothing to offer and nothing to validate a typed value against. Also
                        // inert while stock lots are allocated: the fill is one or the other.
                        disabled={manualFieldsDisabled}
                        value={manualLotNumber}
                        onChange={handleManualLotChange}
                        onInputChange={(_e, value, reason) => {
                          // Only typing clears a pick; 'reset' fires when a selection populates
                          // the input and must not undo it.
                          if (reason === 'input') {
                            setManualLotNumber(value);
                            // Releasing the pick has to drop the BUD the pick supplied. That date
                            // described the abandoned row, and the field becomes editable the
                            // moment the id goes — so a date left behind reads as one the operator
                            // entered, and attaches one lot's expiry to a different lot number.
                            // Only an ADOPTED date goes; one they typed themselves is theirs.
                            //
                            // handlePickDepletedLot cannot do this: it clears the BUD only while
                            // manualPickedLotId is still set, and this handler has already nulled
                            // it by the time the blur reaches there.
                            if (manualPickedLotId) setManualBud('');
                            setManualPickedLotId(null);
                          }
                        }}
                        getOptionLabel={(option) =>
                          typeof option === 'string' ? option : option.lotNumber
                        }
                        isOptionEqualToValue={(option, value) =>
                          (typeof option === 'string' ? option : option.lotNumber) ===
                          (typeof value === 'string' ? value : value?.lotNumber)
                        }
                        renderOption={(props, option) => (
                          <MenuItem {...props} key={option.inventoryLotId}>
                            <Box>
                              <Typography variant='body2'>{option.lotNumber}</Typography>
                              <Typography variant='caption' color='text.secondary'>
                                {option.expirationDate
                                  ? `BUD ${String(option.expirationDate).substring(0, 10)}`
                                  : 'No BUD recorded'}
                                {option.isExpired ? ' · expired' : ''}
                                {isStockedChoice(option)
                                  ? ` · ${formatQty(option.remainingQty)} available`
                                  : ''}
                                {parseFloat(option.unverifiedQty) > 0
                                  ? ` · ${formatQty(option.unverifiedQty)} already in ${SYSTEM_DEFICIT.toLowerCase()}`
                                  : ''}
                              </Typography>
                            </Box>
                          </MenuItem>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label='Lot number'
                            required
                            error={
                              Boolean(manualLotNumber.trim()) && !isValidLotNumber(manualLotNumber)
                            }
                            helperText={
                              // The shared receiving copy when the value breaks the rule — it
                              // names WHICH rule; the ordinary guidance otherwise.
                              getLotHelperText(false, manualLotNumber) || manualLotGuidance
                            }
                          />
                        )}
                        sx={{ flex: '1 1 240px' }}
                      />

                      {/* Re-keyed by hand, and pasteable by nothing: a lot number typed from
                          a vial is the one value in this panel that no later screen can correct,
                          and a claim recorded against a mistyped lot cannot be reconciled by
                          anyone. Absent for a lot PICKED from the list above — that value came
                          from inventory's own row, so there is no typo for a second typing to
                          catch, and demanding one anyway is how a confirmation field becomes
                          something operators learn to defeat by copying. */}
                      {needsLotConfirmation && (
                        <TextField
                          size='small'
                          label='Re-enter lot number'
                          required
                          value={manualConfirmLotNumber}
                          onChange={(e) => setManualConfirmLotNumber(e.target.value)}
                          disabled={manualFieldsDisabled || !manualLotNumber.trim()}
                          inputProps={{
                            ...NO_PASTE_INPUT_PROPS,
                            'data-testid': 'manual-lot-confirm',
                          }}
                          error={lotConfirmFieldError(
                            false,
                            manualLotNumber,
                            manualConfirmLotNumber
                          )}
                          helperText={
                            getLotConfirmHelperText(
                              false,
                              manualLotNumber,
                              manualConfirmLotNumber
                            ) || 'Type it again — paste is disabled'
                          }
                          sx={{ flex: '1 1 240px' }}
                        />
                      )}

                      {/* Disabled once an existing lot is picked: that row's BUD is part of
                          its identity, and editing it here would describe a different lot than
                          the one being claimed. */}
                      <TextField
                        size='small'
                        type='date'
                        label='Expiration date (BUD)'
                        // A stocked lot's BUD is shown from the lot itself, never from the field:
                        // it is matched on the typed number too, and nothing was picked to set it.
                        value={
                          matchedStockLot
                            ? String(matchedStockLot.expirationDate ?? '').substring(0, 10)
                            : manualBud
                        }
                        onChange={(e) => setManualBud(e.target.value)}
                        disabled={
                          manualFieldsDisabled ||
                          Boolean(matchedStockLot) ||
                          (Boolean(manualPickedLotId) && !pickedLotNeedsBud)
                        }
                        InputLabelProps={{ shrink: true }}
                        // Floors the picker at tomorrow: this lot is being dispensed, not
                        // received, so a date already reached describes stock that cannot go out.
                        // Receiving tolerates a short-dated arrival (BUD_LOOKBACK_DAYS); a fill
                        // cannot. Bound only while the field is the operator's to type — a date
                        // adopted from a picked row is not.
                        inputProps={budIsTyped ? { min: getDispensableBudMinIso() } : undefined}
                        error={Boolean(budTooEarly)}
                        helperText={manualBudHelperText}
                        sx={{ flex: '1 1 180px' }}
                      />
                      {/* Read-only, not disabled: the value still has to be legible, because it is
                          the size of the unverified claim this entry creates. It is always the
                          quantity still to allocate — a hand-recorded lot covers the whole of what
                          is left, and a typo here would post a claim for stock that was never
                          dispensed, on a lot nobody can reconcile against a receipt.

                          NOTE: this is what makes one hand-recorded lot cover the entire fill. The
                          panel can still be reopened, but with nothing left to allocate a second
                          manual lot can no longer be added. */}
                      <TextField
                        size='small'
                        type='number'
                        label='Quantity'
                        value={manualQty}
                        inputProps={{
                          readOnly: true,
                          'aria-readonly': true,
                          'data-testid': 'manual-lot-qty',
                        }}
                        helperText='Full quantity to fill'
                        sx={{ flex: '0 0 120px' }}
                      />
                    </Box>

                    {/* Suppressed while loading: the NDC field helper already says so, and a
                        warning Alert reads as something being wrong rather than merely pending.
                        manualError still holds a value, which is what keeps Add lot disabled. */}
                    {manualError && !isLoadingRecordable && (
                      <Alert severity='warning' sx={{ mb: 1.5, py: 0.25 }}>
                        {manualError}
                      </Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button size='small' onClick={closeManualEntry}>
                        Cancel
                      </Button>
                      <LoadingButton
                        size='small'
                        variant='contained'
                        onClick={handleAddManualLot}
                        loading={foreignCheckPending}
                        disabled={Boolean(manualError) || foreignCheckPending}
                      >
                        Add lot
                      </LoadingButton>
                    </Box>
                  </Box>
                )}

                {/* Manually recorded lots live in their own section rather than the FEFO list.
                    They are not available stock: the quantity posts to the unverified bucket, so
                    showing them beside real lots would read as stock on hand. */}
                {manualLots.length > 0 && (
                  <Box
                    sx={{
                      mb: 1.5,
                      border: '1px solid',
                      borderColor: 'warning.light',
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 1,
                        bgcolor: (t) => alpha(t.palette.warning.main, 0.08),
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <WarningAmberIcon fontSize='small' sx={{ color: 'warning.dark' }} />
                      <Typography variant='caption' fontWeight={700} sx={{ color: 'warning.dark' }}>
                        Manually recorded — {SYSTEM_DEFICIT.toLowerCase()}
                      </Typography>
                    </Box>
                    {manualLots.map((lot) => (
                      <Box
                        key={lotKey(lot)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 2,
                          py: 1.25,
                          '&:not(:last-of-type)': {
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          },
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant='body2' fontWeight={600} noWrap>
                            {lot.lotNumber}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {`NDC ${lot.ndc}`}
                            {' · '}
                            {lot.expirationDate
                              ? `exp ${lot.expirationDate}`
                              : 'no expiration date'}
                          </Typography>
                        </Box>
                        {/* POM-6146 fixed the entry panel's quantity at the full fill, so an
                            editable field here would let it straight back out: add at 30, then
                            edit to 5, and the claim no longer matches what was dispensed. The
                            way to change a hand-recorded quantity is to remove the row and
                            re-enter it, which is also the only way to keep the duplicate guard
                            (keyed on NDC + lot number + BUD) meaningful. */}
                        <TextField
                          size='small'
                          type='number'
                          value={lot.qtyToAllocate}
                          inputProps={{
                            readOnly: true,
                            'aria-readonly': true,
                            'aria-label': 'Quantity',
                          }}
                          sx={{ width: 96 }}
                        />
                        <IconButton
                          size='small'
                          aria-label={`Remove lot ${lot.lotNumber}`}
                          onClick={() => handleRemoveLot(lotKey(lot))}
                        >
                          <CloseIcon fontSize='small' />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}

                {!manualMode &&
                  (hasNoLots ? (
                    <Box
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 2,
                        bgcolor: (t) => alpha(t.palette.text.primary, 0.015),
                      }}
                    >
                      <Typography variant='body2' color='text.secondary' gutterBottom>
                        No available lots found for this product at this facility.
                      </Typography>
                      {/* The copy has to match what the button will actually do. Left as a
                          promise of an unassigned fill while that fill is refused, this reads as
                          the dialog being broken rather than as a rule being applied. */}
                      {unassignedFillBlocked ? (
                        <Typography variant='caption' color='text.secondary'>
                          Use{' '}
                          <Typography component='span' variant='caption' fontWeight={600}>
                            Enter lot manually
                          </Typography>{' '}
                          to record the lot from the product in your hand. A fill cannot go out
                          non-lotted while manual entry is available.
                        </Typography>
                      ) : (
                        <Typography variant='caption' color='text.secondary'>
                          This fill will be recorded as{' '}
                          <Typography component='span' variant='caption' fontWeight={600}>
                            unassigned
                          </Typography>{' '}
                          (non-lotted) and reconciled during the next inventory audit.
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}
                    >
                      {isLoading &&
                        Array.from({ length: 4 }).map((_, i) => (
                          <Box
                            // eslint-disable-next-line react/no-array-index-key
                            key={i}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              px: 2,
                              py: 1.5,
                              '&:not(:last-of-type)': {
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                              },
                            }}
                          >
                            <Skeleton variant='circular' width={24} height={24} />
                            <Box sx={{ flex: 1 }}>
                              <Skeleton width='40%' />
                              <Skeleton width='25%' />
                            </Box>
                            <Skeleton width={48} />
                            <Skeleton variant='rounded' width={96} height={34} />
                          </Box>
                        ))}

                      {!isLoading && activeLots.length === 0 && (
                        <Box sx={{ py: 3, textAlign: 'center' }}>
                          <Typography variant='body2' color='text.secondary'>
                            {selectedLots.length > 0
                              ? 'All lots for this NDC are allocated.'
                              : 'No available lots for this NDC.'}
                          </Typography>
                        </Box>
                      )}

                      {!isLoading &&
                        activeLots.map((lot) => {
                          const selected = selectedById.get(lotKey(lot));
                          const isSel = Boolean(selected);
                          // POM-6147 - picking a lot replaces the selection, so a full
                          // allocation no longer disables the other rows: switching lots is the
                          // normal way to change your mind. Hand-recording still disables them,
                          // because the fill is stock OR a claim, never a mix the operator
                          // composed. Already selected rows stay live so it can be cleared.
                          const addDisabled = !isSel && hasManualSelection;
                          let rowCursor = 'pointer';
                          if (isSel) rowCursor = 'default';
                          else if (addDisabled) rowCursor = 'not-allowed';
                          return (
                            <Box
                              key={lotKey(lot)}
                              onClick={
                                isSel || addDisabled ? undefined : () => handleSelectLot(lot)
                              }
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                px: 2,
                                py: 1.25,
                                cursor: rowCursor,
                                opacity: addDisabled ? 0.5 : 1,
                                borderLeft: '3px solid',
                                borderLeftColor: isSel ? 'primary.main' : 'transparent',
                                bgcolor: (t) =>
                                  isSel ? alpha(t.palette.primary.main, 0.05) : 'transparent',
                                transition: 'background-color 150ms ease',
                                '&:not(:last-of-type)': {
                                  borderBottom: '1px solid',
                                  borderColor: 'divider',
                                },
                                '&:hover': {
                                  bgcolor: (t) =>
                                    // eslint-disable-next-line no-nested-ternary
                                    isSel
                                      ? alpha(t.palette.primary.main, 0.08)
                                      : addDisabled
                                        ? 'transparent'
                                        : alpha(t.palette.primary.main, 0.03),
                                },
                              }}
                            >
                              <IconButton
                                size='small'
                                disabled={addDisabled}
                                aria-label={isSel ? 'Clear selected lot' : 'Select lot for fill'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isSel) handleRemoveLot(lotKey(lot));
                                  else handleSelectLot(lot);
                                }}
                                sx={{ color: isSel ? 'primary.main' : 'action.active' }}
                              >
                                {isSel ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                              </IconButton>

                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant='body2' fontWeight={600} noWrap>
                                  {lot.lotNumber}
                                </Typography>
                                <Box
                                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}
                                >
                                  <Typography variant='caption' color='text.secondary'>
                                    Exp {formatDate(lot.expirationDate)}
                                  </Typography>
                                  <ExpiryChip daysUntilExpiry={lot.daysUntilExpiry} />
                                </Box>
                              </Box>

                              <Box sx={{ textAlign: 'right', mr: 0.5 }}>
                                <Typography
                                  variant='caption'
                                  color='text.secondary'
                                  sx={{ display: 'block', lineHeight: 1.2 }}
                                >
                                  Available
                                </Typography>
                                <Typography
                                  variant='body2'
                                  fontWeight={700}
                                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                                >
                                  {formatQty(lot.remainingQty)}
                                </Typography>
                              </Box>

                              <Box
                                sx={{
                                  width: 168,
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                {isSel && (
                                  <>
                                    {/* POM-6147 - derived, never typed: a fill takes everything
                                        this lot can give up to the prescription quantity, and
                                        the remainder becomes an unverified claim. There is no
                                        second lot to move units to, so there is nothing here for
                                        the operator to decide. */}
                                    <TextField
                                      size='small'
                                      type='number'
                                      label='Allocate'
                                      value={selected.qtyToAllocate}
                                      onClick={(e) => e.stopPropagation()}
                                      inputProps={{
                                        readOnly: true,
                                        'aria-readonly': true,
                                        style: { textAlign: 'right' },
                                      }}
                                      sx={{ width: 118 }}
                                    />
                                    <IconButton
                                      size='small'
                                      color='error'
                                      aria-label='Clear selected lot'
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveLot(lotKey(lot));
                                      }}
                                    >
                                      <CloseIcon fontSize='small' />
                                    </IconButton>
                                  </>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                    </Box>
                  ))}

                {!hasNoLots && !manualMode && activeGroup && (
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ display: 'block', mt: 1 }}
                  >
                    {activeGroup.lots.length} {activeGroup.lots.length === 1 ? 'lot' : 'lots'} for
                    this NDC · {lotsData.totalCount} across all NDCs
                  </Typography>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>

      {/* Allocation progress. Outside DialogContent so it stays pinned above the actions while
          the lots above it scroll: it is what the operator is confirming. Same conditions as the
          lot section it summarises. */}
      {allocationCheck === 'none' && !isItemLocked && !hasNoLots && (
        <Box
          sx={{
            mx: 3,
            mt: 2,
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: (t) => alpha(t.palette.text.primary, 0.02),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 1,
              mb: 1,
            }}
          >
            <Typography variant='body2' fontWeight={700}>
              Allocated{' '}
              <Box
                component='span'
                sx={{ color: getAllocationColor(), fontVariantNumeric: 'tabular-nums' }}
              >
                {Math.round(totalAllocated)}
              </Box>{' '}
              / {Math.round(qtyToFill)}
            </Typography>
            <Typography variant='caption' sx={{ color: getAllocationColor(), fontWeight: 600 }}>
              {getAllocationMessage()}
            </Typography>
          </Box>
          <LinearProgress
            variant='determinate'
            value={progressValue}
            sx={{
              height: 8,
              borderRadius: 5,
              bgcolor: (t) => alpha(t.palette.text.primary, 0.08),
              '& .MuiLinearProgress-bar': {
                bgcolor: getAllocationColor(),
                borderRadius: 5,
              },
            }}
          />

          {/* POM-6147 - the fill is allowed to proceed short, so the operator has to
              be told what happens to the difference BEFORE they confirm it. Named
              against the lot, because that is where the claim lands and where an
              inventory user will later enter the missing receipt to clear it. */}
          {shortfallQty > 0 && stockLot && (
            <Alert severity='warning' icon={false} sx={{ mt: 1.5, py: 0.75 }}>
              <Typography variant='caption' sx={{ display: 'block' }}>
                Lot {stockLot.lotNumber} holds {formatQty(stockLot.qtyToAllocate)} of the{' '}
                {formatQty(qtyToFill)} needed. A fill draws from one lot, so the remaining{' '}
                <strong>{formatQty(shortfallQty)}</strong> will be recorded as{' '}
                {SYSTEM_DEFICIT.toLowerCase()}
                against this lot until an inventory user enters the receiving record for it.
              </Typography>
            </Alert>
          )}
        </Box>
      )}

      <Divider sx={{ mt: 2.5 }} />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          variant='outlined'
          onClick={onClose}
          color='inherit'
          disabled={isConfirming || isDiscarding}
        >
          Cancel
        </Button>
        {allocationCheck === 'hasActive' && (
          <Button
            variant='outlined'
            color='error'
            onClick={() => setShowDiscardConfirm(true)}
            disabled={isDiscarding}
          >
            Discard Allocation
          </Button>
        )}
        {allocationCheck === 'none' && (
          <LoadingButton
            variant='contained'
            onClick={() => onConfirm({ selectedLots: lotsForConfirm, unassigned: hasNoLots })}
            disabled={confirmDisabled}
            loading={isConfirming}
          >
            {hasNoLots ? 'Confirm Fill (Unassigned)' : 'Confirm Fill'}
          </LoadingButton>
        )}
        {allocationCheck === 'hasActive' && (
          <LoadingButton
            variant='contained'
            onClick={handleProceedWithExisting}
            disabled={isDiscarding || isActiveAllocationLocked}
            loading={isConfirming}
          >
            Confirm Fill
          </LoadingButton>
        )}
      </DialogActions>

      <DiscardAllocationConfirmDialog
        open={showDiscardConfirm}
        onClose={() => setShowDiscardConfirm(false)}
        onConfirm={handleDiscardConfirm}
        loading={isDiscarding}
      />
    </Dialog>
  );
};

FillProductLotDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  fulfillingPharmacyId: PropTypes.string.isRequired,
  mpi: PropTypes.string.isRequired,
  isConfirming: PropTypes.bool,
  item: PropTypes.shape({
    lineItemId: PropTypes.string,
    itemName: PropTypes.string,
    ndc: PropTypes.string,
    productId: PropTypes.string,
    rxNumber: PropTypes.string,
    strength: PropTypes.string,
    form: PropTypes.string,
    quantity: PropTypes.string,
  }).isRequired,
};

FillProductLotDialog.defaultProps = {
  isConfirming: false,
};

export default FillProductLotDialog;
